import * as React from 'react'

import clsx from 'clsx'

import {
  Badge,
  Button,
  VisuallyHidden,
  ListFilterIcon,
  ChevronDownIcon,
} from '@buffer-mono/popcorn'
import type { ButtonSize, ButtonVariant } from '@buffer-mono/popcorn'

import styles from './FilterButton.module.css'

import type { JSX } from 'react'

const sizeClassNames: Partial<Record<ButtonSize, string>> = {
  medium: styles.medium,
  large: styles.large,
}

type FilterButtonProps = React.ComponentPropsWithoutRef<typeof Button> & {
  icon?: React.ReactNode
  count?: number
  size?: ButtonSize
  variant?: ButtonVariant
  className?: string
  hideChevron?: boolean
}

/**
 * Button to be used for filtering content, it displays cound badge and chevron icon.
 * Utilize it for filtering only, not for selecting.
 */
export const FilterButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  FilterButtonProps
>(
  (
    {
      icon,
      count,
      children,
      className,
      size = 'large',
      variant = 'secondary',
      hideChevron = false,
      'aria-label': ariaLabel,
      ...delegatedProps
    },
    forwardedRef,
  ): JSX.Element => {
    return (
      <Button
        size={size}
        variant={variant}
        className={clsx(sizeClassNames[size], className)}
        aria-label={ariaLabel}
        {...delegatedProps}
        ref={forwardedRef}
      >
        {icon ?? <ListFilterIcon />}
        {ariaLabel ? null : <VisuallyHidden>Filter by</VisuallyHidden>}
        {children}
        {count !== undefined && count > 0 && (
          <Badge className={styles.badge} variant="brand" size="small">
            {count}
          </Badge>
        )}
        {!hideChevron && <ChevronDownIcon className={styles.chevron} />}
      </Button>
    )
  },
)

FilterButton.displayName = 'FilterButton'
